import { useMemo } from 'react';
import { useGetGuestJourneySettingsQuery } from 'api/client-domain/client-domain';
import {
  useGetGuestJourneyDetailsCardsQuery as useGetGuestJourneyDetailsCardsQueryBase,
  useGetGuestJourneyDetailsQuery as useGetGuestJourneyDetailsDataQueryBase,
  useGetGuestJourneyOfferImpactCardsQuery as useGetGuestJourneyOfferImpactCardsQueryBase,
  useGetGuestJourneyOfferImpactSankeyQuery as useGetGuestJourneyOfferImpactSankeyQueryBase,
  useGetGuestJourneyPopulationSummaryQuery as useGetGuestJourneyPopulationSummaryQueryBase,
  useGetGuestJourneyRetentionCardsQuery as useGetGuestJourneyRetentionCardsQueryBase,
  useGetGuestJourneyRetentionConversionTrendsQuery as useGetGuestJourneyRetentionConversionTrendsQueryBase,
  useGetGuestJourneyStageMovementCardsQuery as useGetGuestJourneyStageMovementCardsQueryBase,
  useGetGuestJourneyStageMovementDataQuery as useGetGuestJourneyStageMovementDataQueryBase,
} from './guest-journey-reports';
import {
  GuestJourneyCardsParams,
  GuestJourneyRetentionParams,
} from './guest-journey-reports.types';
import { toStageConfig } from './guest-journey-settings.utils';

/**
 * Transforms the Guest Journey page search params (FE names: `storeIds`,
 * `offer`, `customerType`, …) to the `v2/guest-journey` wire names
 * (`storeIds`, `offerIds`, `population`, …) — the same pattern Email Pulse
 * uses in `email-pulse-hooks.ts`. Each wrapped hook sends only the params its
 * BE endpoint declares.
 */
type SearchParams = Record<string, unknown>;

const toIdArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(String)
    : String(value).split(',').filter(Boolean);

/** `storeIds` passes through unchanged; the `ALL` sentinel omits the filter. */
export const pickStoreIds = (
  params: SearchParams,
): Pick<GuestJourneyCardsParams, 'storeIds'> => {
  if (!params.storeIds) return {};
  const ids = toIdArray(params.storeIds);
  if (ids.length === 0 || ids.includes('ALL')) return {};
  return { storeIds: ids };
};

/**
 * Toolbar date range → the BE `PulseDateRange` fields (`startdate`/`enddate`,
 * bound server-side into the `dateRange` object). The comparison pair is
 * intentionally omitted: no GJ tab surfaces a custom comparison window — the
 * cards' `trend` pills are BE-computed, and Retention's only "compare" control
 * is the `compareToPeriodDays` single-select.
 */
export const pickDateRange = (
  params: SearchParams,
): Pick<GuestJourneyCardsParams, 'startdate' | 'enddate'> => ({
  ...(params.startdate ? { startdate: String(params.startdate) } : {}),
  ...(params.enddate ? { enddate: String(params.enddate) } : {}),
});

/** The Details "Customer Type" filter is the BE `population` enum. */
const POPULATION_BY_CUSTOMER_TYPE: Record<
  string,
  GuestJourneyCardsParams['population']
> = {
  ACTIVE: 'active',
  SLIDING: 'sliding',
  CHURNED: 'churn',
};

export const pickPopulation = (
  params: SearchParams,
): Pick<GuestJourneyCardsParams, 'population'> => {
  const population = POPULATION_BY_CUSTOMER_TYPE[String(params.customerType)];
  return population ? { population } : {};
};

/** Retention `compare` option → the BE `compareToPeriodDays` day count. */
const COMPARE_TO_PERIOD_DAYS: Record<string, number> = {
  PREV_30: 30,
  PREV_60: 60,
  PREV_90: 90,
  SAME_LAST_YEAR: 365,
};

export const toRetentionParams = (
  params: SearchParams,
): GuestJourneyRetentionParams => {
  const compareToPeriodDays = COMPARE_TO_PERIOD_DAYS[String(params.compare)];
  return {
    ...pickStoreIds(params),
    ...pickDateRange(params),
    ...(compareToPeriodDays ? { compareToPeriodDays } : {}),
    ...(params.source && params.source !== 'ALL'
      ? { signUpSource: String(params.source) }
      : {}),
  };
};

/**
 * Wraps a base RTK Query hook with a search-param transform. The base hook is
 * intentionally `any` so the wrapped hook satisfies `useSearchQuery`'s
 * `UseQuery` parameter (same approach as email-pulse's `createQueryHook`).
 */
const createCardsHook =
  (baseHook: any, toParams: (params: SearchParams) => unknown) =>
  (params: SearchParams) =>
    baseHook(toParams(params));

// Population summary cards — one BE endpoint keyed by the `{population}` path
// segment; the wrapped hook injects it alongside `storeIds` (the KPI window is
// pinned server-side to last 90 days).
export const useGetGuestJourneyActiveCardsQuery = createCardsHook(
  useGetGuestJourneyPopulationSummaryQueryBase,
  params => ({ ...pickStoreIds(params), population: 'active' }),
);

export const useGetGuestJourneySlidingCardsQuery = createCardsHook(
  useGetGuestJourneyPopulationSummaryQueryBase,
  params => ({ ...pickStoreIds(params), population: 'sliding' }),
);

export const useGetGuestJourneyChurnCardsQuery = createCardsHook(
  useGetGuestJourneyPopulationSummaryQueryBase,
  params => ({ ...pickStoreIds(params), population: 'churn' }),
);

/**
 * Stage Movement endpoints all take Locations + Date Range + Stage. The stage
 * search-param value is already the BE `stage` enum (`SPEND_TIER_x_VISIT_TIER_y`),
 * so it passes through.
 */
export const toStageMovementParams = (
  params: SearchParams,
): GuestJourneyCardsParams => ({
  ...pickStoreIds(params),
  ...pickDateRange(params),
  ...(params.stage ? { stage: String(params.stage) } : {}),
});

/** Offer Impact endpoints all take Locations + Date Range + Offer. */
export const toOfferImpactParams = (
  params: SearchParams,
): GuestJourneyCardsParams => ({
  ...pickStoreIds(params),
  ...pickDateRange(params),
  ...(params.offer && params.offer !== 'ALL'
    ? { offerIds: String(params.offer) }
    : {}),
});

export const useGetGuestJourneyStageMovementCardsQuery = createCardsHook(
  useGetGuestJourneyStageMovementCardsQueryBase,
  toStageMovementParams,
);

export const useGetGuestJourneyStageMovementDataQuery = createCardsHook(
  useGetGuestJourneyStageMovementDataQueryBase,
  toStageMovementParams,
);

export const useGetGuestJourneyOfferImpactCardsQuery = createCardsHook(
  useGetGuestJourneyOfferImpactCardsQueryBase,
  toOfferImpactParams,
);

export const useGetGuestJourneyOfferImpactSankeyQuery = createCardsHook(
  useGetGuestJourneyOfferImpactSankeyQueryBase,
  toOfferImpactParams,
);

/**
 * The Details tab summary + data endpoints take the same filters: Locations +
 * Date Range + Customer Type (the last mapped to the BE `population` enum). Both
 * wrapped hooks share this transform so the table and its summary cards always
 * query the identical slice.
 */
export const toDetailsParams = (
  params: SearchParams,
): GuestJourneyCardsParams => ({
  ...pickStoreIds(params),
  ...pickDateRange(params),
  ...pickPopulation(params),
});

export const useGetGuestJourneyDetailsCardsQuery = createCardsHook(
  useGetGuestJourneyDetailsCardsQueryBase,
  toDetailsParams,
);

export const useGetGuestJourneyDetailsDataQuery = createCardsHook(
  useGetGuestJourneyDetailsDataQueryBase,
  toDetailsParams,
);

export const useGetGuestJourneyRetentionCardsQuery = createCardsHook(
  useGetGuestJourneyRetentionCardsQueryBase,
  toRetentionParams,
);

export const useGetGuestJourneyRetentionConversionTrendsQuery = createCardsHook(
  useGetGuestJourneyRetentionConversionTrendsQueryBase,
  toRetentionParams,
);

/**
 * The per-client stage taxonomy, fetched once (RTK Query caches across tabs)
 * and derived into the display view-model. Every view that renders tier names
 * or thresholds binds through this hook — `stageConfig` is undefined while
 * settings load.
 */
export const useGuestJourneyStageConfig = () => {
  const { data: settings, isFetching } = useGetGuestJourneySettingsQuery();
  const stageConfig = useMemo(
    () => (settings ? toStageConfig(settings) : undefined),
    [settings],
  );
  return { settings, stageConfig, isFetching };
};
